import { QuestionCircleOutlined } from '@ant-design/icons';
import { Space, Tooltip } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { useDepartments } from '@/common/useDepartments';
import type { GetStatusesQuery } from '@/generated/graphql/graphql';
import { useExpressOrders } from '../context/ExpressOrdersProvider';

interface StatusFilterLinksProps {
  statusesData: GetStatusesQuery;
  selectedStatus: string;
  onStatusChange: (statusName: string) => void;
}

export const StatusFilterLinks: React.FC<StatusFilterLinksProps> = ({
  statusesData,
  selectedStatus,
  onStatusChange,
}) => {
  const { selectedDepartmentId } = useDepartments();
  const { ordersByDepartment } = useExpressOrders();

  const statuses = useMemo(() => statusesData.status, [statusesData]);

  const hasExpressNew = Boolean(ordersByDepartment.get(selectedDepartmentId)?.length ?? 0);
  const hasExpressDriverAssigned = Boolean(
    (statusesData.DriverAssignedExpress?.aggregate?.count ?? 0) > 0
  );

  const filteredStatuses = useMemo(
    () => statuses.filter((status) => (status.orders_aggregate?.aggregate?.count || 0) > 0),
    [statuses]
  );

  const getStatusCounts = (statusName: string) => {
    const status = statuses.find((s) => s.name === statusName);
    if (!status) return null;

    const totalCount = status.orders_aggregate?.aggregate?.count || 0;
    let expressCount = 0;

    switch (statusName) {
      case 'DriverAssigned':
        expressCount = statusesData.DriverAssignedExpress?.aggregate?.count || 0;
        break;
      case 'DriverConfirmed':
        expressCount = statusesData.DriverConfirmedExpress?.aggregate?.count || 0;
        break;
      case 'DriverOnTheWay':
        expressCount = statusesData.DriverOnTheWayExpress?.aggregate?.count || 0;
        break;
      default:
        return null;
    }

    if (totalCount === 0) return null;

    return { totalCount, expressCount };
  };

  const renderStatusText = (status: GetStatusesQuery['status'][0]) => {
    const counts = getStatusCounts(status.name);

    if (counts !== null) {
      return `${status.name} (${counts.totalCount}/${counts.expressCount})`;
    }

    return `${status.name} (${status.orders_aggregate?.aggregate?.count || 0})`;
  };

  if (filteredStatuses.length === 0) {
    return (
      <div
        style={{
          backgroundColor: '#1677ff',
          padding: '12px',
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#ffffff', fontSize: 16 }}>No orders available</span>
        <Tooltip title="Statuses with zero orders are hidden">
          <QuestionCircleOutlined style={{ marginLeft: 8, color: '#ffffff', fontSize: 16 }} />
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#1677ff',
        padding: '12px',
        marginBottom: 4,
      }}
    >
      <Space wrap>
        <button
          key="ALL"
          type="button"
          onClick={() => onStatusChange('ALL')}
          style={{
            fontWeight: selectedStatus === 'ALL' ? 'bold' : 'normal',
            color: '#ffffff',
            textDecoration: selectedStatus === 'ALL' ? 'underline' : 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: 16,
            position: 'relative',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          ALL
        </button>
        {filteredStatuses.map((status) => (
          <button
            key={status.name}
            type="button"
            onClick={() => onStatusChange(status.name)}
            style={{
              fontWeight: selectedStatus === status.name ? 'bold' : 'normal',
              color: '#ffffff',
              textDecoration: selectedStatus === status.name ? 'underline' : 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: 16,
              position: 'relative',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {renderStatusText(status)}
            {((status.name.toUpperCase() === 'NEW' && hasExpressNew) ||
              (status.name.toUpperCase() === 'DRIVERASSIGNED' && hasExpressDriverAssigned)) && (
              <span
                data-testid="express-indicator"
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 10,
                  height: 10,
                  backgroundColor: 'red',
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              />
            )}
          </button>
        ))}
        <Tooltip title="Statuses with zero orders are hidden">
          <QuestionCircleOutlined style={{ color: '#ffffff', fontSize: 16 }} />
        </Tooltip>
      </Space>
    </div>
  );
};
